import React, { useState, useEffect, useCallback } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { watchlistActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { LoadPanel } from 'devextreme-react/load-panel';
import { WatchlistApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faExclamationTriangle,
    faChevronRight,
    faChevronLeft,
    faChevronDown,
    faChevronUp,
} from '@fortawesome/free-solid-svg-icons';
import DataGrid, { Column, Paging, Pager, Selection } from 'devextreme-react/data-grid';
import { getLoanTypeShortName } from '../../loans/common/LoanFunctions';
import { WatchlistSummaryTypeOptions } from '@apply/constants';
import { handleAdminError, handleAdminSuccess } from '../../common/SharedFunctions';
import { ConfirmModal } from '@apply/ui';
import { RefreshEvents } from '@apply/ui';
import { ColorBox } from 'devextreme-react/color-box';
import { CompactPicker } from 'react-color';
import { hasPermission } from '@apply/ui';
import { PermissionType } from '@apply/constants';
import { LoanType } from '../../loans/common/LoanConstants';
import { addressTooltipText } from '../../common/SharedFunctions';
import { Tooltip } from 'devextreme-react/tooltip';

const GroupItemsModal = () => {
    const { isOpen, watchlistId, summaryTypeId, groupId = 0 } = useSelector((state) => state.watchlists.groupItemsModal);
    const { dispatch } = store;
    const watchlistApi = new WatchlistApi.WatchlistApi(applicationInsights);
    const refreshEvents = new RefreshEvents();

    const styles = {
        buttonMarginRight: { marginRight: '10px' },
        buttonContainer: {
            textAlign: 'right',
            borderTop: '2px solid silver',
            padding: '10px 10px',
        },
        formColumn: { paddingBottom: '10px' },
        formRow: { marginBottom: '0' },
        selection: { textAlign: 'center', marging: '0', padding: '0' },
    };

    const colorPalette = [
        '#FFEB3B', // Bright Yellow
        '#FFEE58', // Lighter Yellow
        '#FFC107', // Amber
        '#FFD54F', // Lighter Amber
        '#FF9800', // Orange
        '#FFB74D', // Lighter Orange
        '#FF7043', // Lightened Deep Orange
        '#FF8A65', // Lighter Deep Orange
        '#FF6F61', // Lightened Red
        '#EF5350', // Lighter Red
        '#F06292', // Lightened Pink
        '#F48FB1', // Lighter Pink
        '#BA68C8', // Lightened Purple
        '#CE93D8', // Lighter Purple
        '#9575CD', // Lightened Deep Purple
        '#B39DDB', // Lighter Deep Purple
        '#7986CB', // Lightened Indigo
        '#9FA8DA', // Lighter Indigo
        '#64B5F6', // Lightened Blue
        '#90CAF9', // Lighter Blue
        '#4FC3F7', // Lightened Light Blue
        '#81D4FA', // Lighter Light Blue
        '#4DD0E1', // Lightened Cyan
        '#80DEEA', // Lighter Cyan
        '#4DB6AC', // Lightened Teal
        '#80CBC4', // Lighter Teal
        '#81C784', // Lightened Green
        '#A5D6A7', // Lighter Green
        '#AED581', // Lightened Light Green
        '#C5E1A5', // Lighter Light Green
        '#DCE775', // Lightened Lime
        '#E6EE9C', // Lighter Lime
    ];

    const [watchlist, set_watchlist] = useState(null);
    const [group, set_group] = useState(null);
    const [availableItems, set_availableItems] = useState([]);
    const [groupItems, set_groupItems] = useState([]);
    const [selectedAvailableIds, set_selectedAvailableIds] = useState([]);
    const [selectedGroupIds, set_selectedGroupIds] = useState([]);
    const [notes, set_notes] = useState('');
    const [paletteColor, set_paletteColor] = useState('');
    const [loadingMessage, set_loadingMessage] = useState('');

    //Submit confirmation
    const [submitConfirmOpen, set_submitConfirmOpen] = useState(false);
    const [submitConfirmMessage, set_submitConfirmMessage] = useState('');

    //Delete confirmation
    const [deleteConfirmOpen, set_deleteConfirmOpen] = useState(false);
    const [deleteConfirmMessage, set_deleteConfirmMessage] = useState('This group will be deleted on a go-forward basis.');

    const _handleClose = () =>
        dispatch(
            watchlistActions.setGroupItemsModal({
                groupItemsModal: { isOpen: false },
            })
        );

    const loadWatchlistAsync = async (watchlistId) => {
        try {
            set_loadingMessage('Loading watchlist info...');
            const response = await watchlistApi.getWatchlistById(watchlistId);
            if (response.statusCode !== 200) {
                throw new Error('Failed to retrieve watchlist. Unexpected response.');
            }
            set_watchlist(response.result);
        } catch (ex) {
            handleAdminError(
                'An unexpected error occurred and watchlist info could no be loaded! Please try refreshing the page.'
            );
        } finally {
            set_loadingMessage('');
        }
    };

    const getGroupAsync = async (groupId, watchlistId, summaryTypeId) => {
        try {
            set_loadingMessage('Loading...');
            const response = await watchlistApi.getItemGroup(watchlistId, groupId, summaryTypeId);
            if (response.statusCode !== 200) {
                throw new Error('Failed to retrieve group information. Unexpected response.');
            }

            const grp = response.result;
            set_group(grp);
            set_notes(grp.notes);
            grp?.color && set_paletteColor(grp.color);
            set_availableItems(grp.availableItems);
            set_groupItems(grp.groupItems);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error occurred. Please try again.');
        } finally {
            set_loadingMessage('');
        }
    };

    useEffect(() => {
        resetState();
    }, [isOpen]);

    const resetState = () => {
        set_availableItems([]);
        set_groupItems([]);
        set_selectedAvailableIds([]);
        set_selectedGroupIds([]);
        set_notes('');
        set_paletteColor('');
        set_group(null);
        set_watchlist(null);
    };

    useEffect(() => {
        if (watchlistId) {
            loadWatchlistAsync(watchlistId);
        }

        if (!summaryTypeId) {
            return;
        }

        getGroupAsync(groupId, watchlistId, summaryTypeId);
    }, [groupId, watchlistId, summaryTypeId]);

    const onLoanTypeCellRender = (e) => {
        if (e.data.isRental) {
            return getLoanTypeShortName(LoanType.Rental);
        }
        return getLoanTypeShortName(e.data.loanTypeId);
    };

    const onBFINumberRender = (e) => {
        const targetId = `ac_${e.data.id}`;
        const text = addressTooltipText(e.data.propertyAddress);
        return (
            <>
                <div id={targetId}>{e.value}</div>
                {e.data.propertyId && (
                    <Tooltip
                        target={'#' + targetId}
                        showEvent='mouseenter'
                        hideEvent='mouseleave'
                        hideOnOutsideClick={false}
                    >
                        {text}
                    </Tooltip>
                )}
            </>
        );
    };

    const getSummaryTypeName = () => {
        var st = WatchlistSummaryTypeOptions.find((a) => a.id === summaryTypeId);
        return st?.name;
    };

    const updateGroupAsync = async () => {
        const model = {
            id: group.id,
            watchlistId,
            notes: notes ? notes : null,
            color: paletteColor,
            items: [],
        };
        const lns = sortItems(groupItems);
        lns.forEach((i) => {
            model.items.push({ loanId: i.loanId, propertyId: i.propertyId, sortOrder: i.sortOrder });
        });

        try {
            set_loadingMessage('Saving Group details...');
            const response = await watchlistApi.updateItemGroup(model);
            if (response.statusCode != 200) {
                throw new Error('Unexpected return code for updateGroup method');
            }
            handleAdminSuccess('Group saved successfully.');

            refreshEvents.dispatchRefreshEvent();

            setTimeout(() => {
                _handleClose();
            }, 600);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error occured! Please try again.');
        } finally {
            set_loadingMessage('');
        }
    };

    const deleteGroupOnThisAndFutureWatchlists = async () => {
        const model = {
            id: group.id,
            watchlistId,
        };

        try {
            set_loadingMessage('Removing Watchlist Group...');
            const response = await watchlistApi.deleteGroupOnThisAndFutureWatchlists(model);
            if (response.statusCode != 200) {
                throw new Error('Unexpected return code for deleteGroupOnThisAndFutureWatchlists method');
            }
            handleAdminSuccess('Item Group removed successfully.');

            refreshEvents.dispatchRefreshEvent();

            setTimeout(() => {
                _handleClose();
            }, 600);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error occured! PLease try again.');
        } finally {
            set_loadingMessage('');
        }
    };

    const onSubmit = () => {
        if (group.id == 0 && groupItems.length === 0) {
            return;
        }

        //show messages for different Entity Ids or multiple statuses.
        const uniqueEntityIds = [...new Set(groupItems.map((item) => item.entityId))];
        const uniqueStatuses = [...new Set(groupItems.map((item) => item.statusId))];

        let msg = '';
        if (uniqueEntityIds.length > 1) {
            msg = '<li>Not all items selected are for the the same borrower.</li>';
        }

        if (uniqueStatuses.length > 1) {
            msg += '<li>Not all items in the same Watchlist section. Group will be broken up as applicable</li>';
        }

        if (msg) {
            msg = `<ul>${msg}</ul><div>Do you want to continue?</div>`;
            set_submitConfirmMessage(msg);
            set_submitConfirmOpen(true);
            return;
        }

        updateGroupAsync();
    };

    const onDelete = () => {
        if (group.id == 0) {
            return;
        }
        set_deleteConfirmOpen(true);
        return;
    };

    const _handleSubmitConfirmationConfirm = async () => {
        set_submitConfirmMessage('');
        set_submitConfirmOpen(false);
        await updateGroupAsync();
    };

    const _handleSubmitConfirmationCancel = () => {
        set_submitConfirmMessage('');
        set_submitConfirmOpen(false);
    };

    const _handleDeleteConfirmationConfirm = async () => {
        set_deleteConfirmOpen(false);
        await deleteGroupOnThisAndFutureWatchlists();
    };

    const _handleDeleteConfirmationCancel = () => {
        set_deleteConfirmOpen(false);
    };

    const onAvailableItemsSelected = useCallback(
        ({ selectedRowKeys: changedRowKeys, selectedRowsData }) => {
            set_selectedAvailableIds([...new Set(selectedRowsData.map((item) => item.id))]);
        },
        [set_selectedAvailableIds]
    );

    const onGroupItemsSelected = useCallback(
        ({ selectedRowKeys: changedRowKeys, selectedRowsData }) => {
            set_selectedGroupIds([...new Set(selectedRowsData.map((item) => item.id))]);
        },
        [set_selectedGroupIds]
    );

    const sortItems = (lns) => {
        lns = [...lns];
        lns.forEach((l) => {
            if (!l.sortOrder) {
                l.sortOrder = 0;
            }
        });
        lns.sort((a, b) => a.sortOrder - b.sortOrder);
        return lns;
    };

    const moveItemUp = (id) => {
        const array = [...groupItems];
        const ln = array.find((a) => a.id === id);
        if (!ln) {
            return;
        }

        const oldSortOrder = ln.sortOrder;
        const newSortOrder = ln.sortOrder - 1;
        if (newSortOrder < 1) {
            return;
        }

        var previousItem = array.find((a) => a.sortOrder === newSortOrder);
        ln.sortOrder = newSortOrder;
        if (previousItem) {
            previousItem.sortOrder = oldSortOrder;
        }
        set_groupItems(array);
    };

    const moveItemDown = (id) => {
        const array = [...groupItems];
        const ln = array.find((a) => a.id === id);
        if (!ln) {
            return;
        }

        const oldSortOrder = ln.sortOrder;
        const newSortOrder = ln.sortOrder + 1;
        if (newSortOrder < 1) {
            return;
        }

        var previousItem = array.find((a) => a.sortOrder === newSortOrder);
        ln.sortOrder = newSortOrder;
        if (previousItem) {
            previousItem.sortOrder = oldSortOrder;
        }
        set_groupItems(array);
    };

    const renderSortedActions = (e) => {
        const maxSortOrder = Math.max(...groupItems.map((o) => o.sortOrder));
        return (
            <div>
                <button
                    class='hollow'
                    title='Move item up'
                    disabled={!canEdit() || e.data.sortOrder <= 1}
                >
                    <FontAwesomeIcon
                        icon={faChevronUp}
                        onClick={(event) => moveItemUp(e.data.id)}
                    />
                </button>
                &nbsp;
                <button
                    class='hollow'
                    title='Move item down'
                    disabled={!canEdit() || e.data.sortOrder >= maxSortOrder}
                >
                    <FontAwesomeIcon
                        icon={faChevronDown}
                        onClick={(event) => moveItemDown(e.data.id)}
                    />
                </button>
            </div>
        );
    };

    const addItem = useCallback(() => {
        let sortOrder = groupItems.length + 1;
        const source = [...availableItems];
        const target = [...groupItems];
        selectedAvailableIds.forEach((id) => {
            const index = source.findIndex((l) => l.id === id);
            if (index >= 0) {
                const ln = source[index];
                ln.sortOrder = sortOrder;
                sortOrder++;
                source.splice(index, 1);
                target.push(ln);
            }
        });
        set_availableItems(source);
        set_groupItems(target);
        set_selectedAvailableIds([]);
    }, [selectedAvailableIds, availableItems, groupItems]);

    const removeItem = useCallback(() => {
        const source = [...groupItems];
        const target = [...availableItems];
        selectedGroupIds.forEach((id) => {
            const index = source.findIndex((l) => l.id === id);
            if (index >= 0) {
                const ln = source[index];
                ln.sortOrder = 0;
                source.splice(index, 1);
                target.push(ln);
            }
        });
        set_groupItems(source);
        set_availableItems(target);
        set_selectedGroupIds([]);
    }, [selectedGroupIds, availableItems, groupItems]);

    const onColorChanged = useCallback(
        (e) => {
            set_paletteColor(e.hex);
        },
        [paletteColor]
    );

    const canEdit = () => {
        return hasPermission(PermissionType.WatchlistEdit);
    };

    if (!isOpen || !watchlistId || !summaryTypeId) {
        return '';
    }

    if (!group) {
        return;
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex={-1}
                            role='dialog'
                            style={{ width: '1100px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <div
                                    className='page-header'
                                    role='banner'
                                >
                                    <h1 style={{ whiteSpace: 'nowrap' }}>
                                        {group.id === 0 ? 'Create New Watchlist Group' : 'Edit Watchlist Group'}
                                    </h1>
                                    <button
                                        type='button'
                                        className='close'
                                        data-dismiss='modal'
                                        aria-label='Close'
                                        onClick={_handleClose}
                                    >
                                        <span aria-hidden='true'>&times;</span>
                                    </button>
                                </div>
                                <div
                                    className='standard-form no-search'
                                    style={{ padding: '0', margin: '0' }}
                                >
                                    <div
                                        className='main-content'
                                        style={{
                                            padding: '0rem 1rem',
                                            margin: '0 10px',
                                        }}
                                    >
                                        <div
                                            style={{
                                                marginTop: '5px',
                                                padding: '10px',
                                                textAlign: 'center',
                                                color: 'darkred',
                                                backgroundColor: '#f4f4f4',
                                                fontSize: 'smaller',
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            &nbsp;&nbsp;Only ungrouped {getSummaryTypeName()} Loans / Properties in the current
                                            Watchlist that are Uncured or Outstanding are shown.
                                        </div>

                                        <div
                                            className='row'
                                            style={{ marginTop: '10px' }}
                                        >
                                            <div
                                                className='columns small-12 large-5'
                                                style={styles.formColumn}
                                            >
                                                <label>Available Loans / Properties</label>
                                                <DataGrid
                                                    dataSource={availableItems}
                                                    key={'id'}
                                                    applicationInsights={applicationInsights}
                                                    rowAlternationEnabled={true}
                                                    showColumnHeaders={true}
                                                    showRowLines={true}
                                                    showBorders={true}
                                                    onSelectionChanged={onAvailableItemsSelected}
                                                    height={290}
                                                    noDataText='No available Loans or Properties'
                                                >
                                                    <Selection
                                                        mode='multiple'
                                                        showCheckBoxesMode='always'
                                                        allowSelectAll={true}
                                                    />
                                                    <Column
                                                        dataField='bfiNumber'
                                                        caption='BFI #'
                                                        dataType='string'
                                                        width={80}
                                                        cellRender={onBFINumberRender}
                                                    />
                                                    <Column
                                                        dataField='borrowerEntity'
                                                        caption='Entity'
                                                    />
                                                    <Column
                                                        dataField='loanTypeId'
                                                        caption='Type'
                                                        cellRender={onLoanTypeCellRender}
                                                        alignment='left'
                                                        width={60}
                                                    />
                                                    <Paging pageSize={10} />
                                                    <Pager
                                                        showPageSizeSelector={true}
                                                        allowedPageSizes={[10]}
                                                        showNavigationButtons={true}
                                                        showInfo={true}
                                                    />
                                                </DataGrid>
                                                <div
                                                    style={{
                                                        textAlign: 'right',
                                                        paddingTop: '5px',
                                                    }}
                                                >
                                                    <button
                                                        class='hollow'
                                                        title='Add selected Items to Group'
                                                        disabled={!canEdit() || selectedAvailableIds.length === 0}
                                                        onClick={(event) => addItem()}
                                                    >
                                                        Add to Group&nbsp;
                                                        <FontAwesomeIcon icon={faChevronRight} />
                                                    </button>
                                                </div>
                                            </div>
                                            <div
                                                className='columns small-12 large-7'
                                                style={styles.formColumn}
                                            >
                                                <label>
                                                    Items in Group (Item at top is considered the primary Loan / Property)
                                                </label>
                                                <DataGrid
                                                    dataSource={sortItems(groupItems)}
                                                    key={'id'}
                                                    applicationInsights={applicationInsights}
                                                    rowAlternationEnabled={true}
                                                    showColumnHeaders={true}
                                                    showRowLines={true}
                                                    showBorders={true}
                                                    onSelectionChanged={onGroupItemsSelected}
                                                    height={290}
                                                    noDataText='No Loans / Properties in group'
                                                >
                                                    <Selection
                                                        mode='multiple'
                                                        showCheckBoxesMode='always'
                                                        allowSelectAll={true}
                                                    />
                                                    <Column
                                                        dataField='bfiNumber'
                                                        caption='BFI #'
                                                        dataType='string'
                                                        width={80}
                                                        allowSorting={false}
                                                        cellRender={onBFINumberRender}
                                                    />
                                                    <Column
                                                        dataField='borrowerEntity'
                                                        caption='Entity'
                                                        allowSorting={false}
                                                    />

                                                    <Column
                                                        dataField='loanTypeId'
                                                        caption='Type'
                                                        cellRender={onLoanTypeCellRender}
                                                        alignment='left'
                                                        width={60}
                                                    />
                                                    <Column
                                                        cellRender={renderSortedActions}
                                                        width={80}
                                                        alignment={'center'}
                                                    />
                                                    <Paging pageSize={10} />
                                                    <Pager
                                                        showPageSizeSelector={true}
                                                        allowedPageSizes={[10]}
                                                        showNavigationButtons={true}
                                                        showInfo={true}
                                                    />
                                                </DataGrid>
                                                <div
                                                    style={{
                                                        paddingTop: '5px',
                                                    }}
                                                >
                                                    <button
                                                        class='hollow'
                                                        title='Remove selected Items from Group'
                                                        disabled={!canEdit() || selectedGroupIds.length === 0}
                                                        onClick={(event) => removeItem()}
                                                    >
                                                        <FontAwesomeIcon icon={faChevronLeft} />
                                                        &nbsp;Remove from Group
                                                    </button>
                                                    {groupItems.length == 1 && (
                                                        <div
                                                            style={{
                                                                textAlign: 'center',
                                                                color: 'darkred',
                                                                fontSize: 'smaller',
                                                                float: 'right',
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                                            &nbsp;&nbsp;You must have at least two items selected to group.
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {groupItems.length ? (
                                        <>
                                            <div
                                                className='row'
                                                style={{
                                                    marginLeft: '15px',
                                                    marginRight: '15px',
                                                }}
                                            >
                                                <div
                                                    className='columns small-12 large-3'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='color'>
                                                        Background Color <span className='req'>*</span>
                                                    </label>

                                                    <CompactPicker
                                                        color={paletteColor}
                                                        onChange={onColorChanged}
                                                        colors={colorPalette}
                                                    />
                                                </div>
                                                <div
                                                    className='columns small-12 large-9'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='notes'>Notes</label>
                                                    <textarea
                                                        id='notes'
                                                        rows='4'
                                                        value={notes}
                                                        maxLength={1000}
                                                        placeholder='Enter any notes relevant to the entire group...'
                                                        onChange={(e) => set_notes(e.target.value)}
                                                        disabled={!canEdit()}
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <></>
                                    )}

                                    <div
                                        className='button-footer'
                                        style={styles.buttonContainer}
                                    >
                                        {(group.id == 0 || (group.id > 0 && (groupItems.length ?? 0) != 0)) && (
                                            <button
                                                type='button'
                                                className='button'
                                                data-dismiss='modal'
                                                onClick={onSubmit}
                                                disabled={
                                                    !canEdit() ||
                                                    (group.id === 0 && (groupItems.length ?? 0) <= 1) ||
                                                    (group.id > 0 && (groupItems.length ?? 0) == 1) ||
                                                    !paletteColor
                                                }
                                            >
                                                Submit
                                            </button>
                                        )}
                                        {group.id > 0 && (groupItems.length ?? 0) == 0 && (
                                            <button
                                                type='button'
                                                className='button'
                                                data-dismiss='modal'
                                                onClick={onDelete}
                                                disabled={!canEdit()}
                                            >
                                                Delete
                                            </button>
                                        )}
                                        &nbsp;
                                        <button
                                            type='button'
                                            className='button hollow'
                                            data-dismiss='modal'
                                            onClick={_handleClose}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={!!loadingMessage}
                message={loadingMessage}
            />
            <ConfirmModal
                isOpen={submitConfirmOpen}
                text={submitConfirmMessage}
                confirmCallback={_handleSubmitConfirmationConfirm}
                cancelCallback={_handleSubmitConfirmationCancel}
            />
            <ConfirmModal
                isOpen={deleteConfirmOpen}
                text={deleteConfirmMessage}
                confirmCallback={_handleDeleteConfirmationConfirm}
                cancelCallback={_handleDeleteConfirmationCancel}
            />
        </>
    );
};

export default GroupItemsModal;
